import type { CSSProperties } from 'react';
import { DateTime } from '@ksyos/date-time';
import { useEffect, useState } from 'react';
import { DatePicker, Localization } from 'react-widgets';
import { DateLocalizer } from 'react-widgets/IntlLocalizer';
import { css, styled } from 'styled-components';
import { getGlobalize, getSelectedLanguage, parseDateInput } from '@frontend/translate';
import type { IFieldProps } from '../elements/Field';
import type { FormContextProps } from '../elements/formContext';
import { Feedback } from '../elements/common';
import { Field } from '../elements/Field';
import { FormContextConsumer } from '../elements/formContext';

export interface IDateInputProps {
    birthDate?: boolean;
    color?: 'magenta' | 'blue';
    disabled?: boolean;
    error?: string;
    hasMaxWidth?: boolean;
    id?: string;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
    maxDate?: string | null;
    minDate?: string | null;
    onChange: (value: string | null) => void;
    onDoValidate?: (date: string | null) => void;
    onEnterPress?: (date: string | null) => void;
    placeholder?: string;
    popupPropsStyle?: CSSProperties;
    showOutOfBoundErrorsValidation?: boolean;
    size?: 'medium' | 'large';
    value: string | null | undefined;
    variant?: 'outline' | 'flushed';
}

export function DateInput(props: FormContextProps<IDateInputProps>) {
    return (
        <FormContextConsumer {...props}>{(properties) => <IndependentDateInput {...properties} />}</FormContextConsumer>
    );
}

export function DateField(props: FormContextProps<IDateInputProps & IFieldProps>) {
    return (
        <Field {...props}>
            <DateInput {...props} />
        </Field>
    );
}

export function IndependentDateInput(props: IDateInputProps) {
    const translate = getSelectedLanguage().calendarComponent;

    const dateFormatter = getGlobalize().dateFormatter();
    const [show, setShow] = useState(true);
    const {
        birthDate,
        disabled,
        error,
        color = 'blue',
        id,
        inputProps,
        maxDate,
        minDate,
        onChange,
        onDoValidate,
        onEnterPress,
        placeholder,
        showOutOfBoundErrorsValidation,
        value,
        variant,
        popupPropsStyle,
        size = 'medium',
    } = props;

    // setShow re-renders component to refresh underlying input
    // onChange(null) has no effect over wrong manually typed input
    useEffect(() => {
        if (!show) {
            setShow(true);
        }
    });

    const datePickerProps = {
        className: computeClassName(),
        color: color,
        disabled: disabled,
        error: error,
        id,
        inputProps,
        max: maxDate ? new Date(maxDate) : undefined,
        min: minDate ? new Date(minDate) : undefined,
        onChange: validateAndSetDate,
        onKeyPress: handleKeyPress,
        parse: (input: string) => parseDateInput(input, birthDate ?? false),
        popupProps: { style: popupPropsStyle },
        size,
        style: props.hasMaxWidth ? datePickerStyle[size] : undefined,
        value: value ? DateTime.from(value).toDate() : null,
        valueFormat: dateFormatter,
        ...({ placeholder: placeholder } as any),
    };

    return (
        <>
            {show && (
                <Localization
                    date={new DateLocalizer({ culture: 'nl', firstOfWeek: 1 })}
                    messages={{
                        dateButton: translate.selectDate,
                        moveBack: translate.moveBack,
                        moveForward: translate.moveForward,
                        moveToday: translate.today,
                    }}
                >
                    {variant === 'outline' ? (
                        <StyledDatePicker
                            $color={color}
                            $hasError={Boolean(error)}
                            $size={size}
                            key="input"
                            {...datePickerProps}
                        />
                    ) : (
                        <DatePicker
                            key="input"
                            {...datePickerProps}
                        />
                    )}
                    <Feedback
                        error={error}
                        key="feedback"
                    />
                </Localization>
            )}
        </>
    );

    function handleKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
        const { target } = event;

        if (event.key !== 'Enter') {
            return;
        }

        const input = (target as HTMLInputElement).value?.trim();
        const date = input ? DateTime.from(parseDateInput(input, birthDate ?? false)).format('YYYY-MM-DD') : null;

        onChange(date);
        onDoValidate?.(date);
        onEnterPress?.(date);
    }

    function computeClassName(): string | undefined {
        if (props.variant !== 'outline') {
            return;
        }

        let className = 'form-datefield--outline-focus';
        if (props.error) {
            className += ' form-datefield--outline-error';
        }

        return className;
    }

    function validateAndSetDate(date: Date | null | undefined) {
        const isDateValid = date && date instanceof Date && !isNaN(date.getTime());

        const formattedDate = isDateValid ? DateTime.from(date!).format('YYYY-MM-DD') : null;

        const isDateOutsideLimits =
            formattedDate &&
            ((maxDate && DateTime.from(maxDate).format('YYYY-MM-DD') < formattedDate) ||
                (minDate && DateTime.from(minDate).format('YYYY-MM-DD') > formattedDate));

        if (isDateValid && (!isDateOutsideLimits || (isDateOutsideLimits && showOutOfBoundErrorsValidation))) {
            onChange(formattedDate);
            onDoValidate?.(formattedDate);
        } else {
            onChange(null);
            setShow(false);
            onDoValidate?.(null);
        }
    }
}

const datePickerStyle = {
    large: { maxWidth: '168px' },
    medium: { maxWidth: '155px' },
} as const;

const StyledDatePicker = styled(DatePicker)<{
    $color: 'magenta' | 'blue';
    $hasError: boolean;
    $size: 'medium' | 'large';
}>`
    .rw-widget-container {
        border-width: 1px;
        border-style: solid;
        border-radius: 4px;
        padding: 0 12px;
        border-color: ${(props) => (props.$hasError ? props.theme.colors.orange700 : props.theme.colors.neutral600)};
        background-color: white;
    }

    .rw-widget-picker {
        height: 40px;
        ${(props) =>
            props.$size === 'medium' &&
            css`
                ${props.theme.mediaQueries.small} {
                    height: 38px;
                }
            `}
    }

    .rw-widget-input {
        font-size: 16px;
        ${(props) =>
            props.$size === 'medium' &&
            css`
                ${props.theme.mediaQueries.small} {
                    font-size: 14px;
                }
            `}
        font-weight: 400;
    }

    .rw-picker-caret,
    .rw-picker-btn {
        color: ${(props) => props.$color && props.theme.colors[`${props.$color}700`]};
    }

    .rw-placeholder,
    .rw-input::placeholder {
        color: ${(props) => props.theme.colors.neutral400};
        opacity: 1;
    }
`;
